import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ThirdPartyClientService from '../../../core/service/ThirdPartyClientService';

const ThirdPartyClientModal = ({ t, show, onHide, clientId, companyId, companyName, onSuccess }) => {
  const [formData, setFormData] = useState({
    companyId: companyId || '',
    thirdPartyName: '',
    description: '',
    allowedApis: [],
    metadata: ''
  });
  const [allowedApisInput, setAllowedApisInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [displayCompanyName, setDisplayCompanyName] = useState(companyName || '');

  useEffect(() => {
    if (show) {
      setDisplayCompanyName(companyName || '');
      if (clientId) {
        loadClientData();
      } else {
        resetForm();
      }
    }
  }, [show, clientId, companyName]);

  const loadClientData = () => {
    setLoading(true);
    ThirdPartyClientService.getClient(clientId)
      .then((response) => {
        if (response.status === 200 && response.data) {
          const data = response.data;
          setFormData({
            companyId: data.companyId || companyId || '',
            thirdPartyName: data.thirdPartyName || '',
            description: data.description || '',
            allowedApis: data.allowedApis || [],
            metadata: data.metadata || ''
          });
          setAllowedApisInput(data.allowedApis ? data.allowedApis.join(', ') : '');
          if (data.companyName) {
            setDisplayCompanyName(data.companyName);
          }
        }
      })
      .catch((error) => {
        toast.error(t("ThirdPartyClientModal.loadError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetForm = () => {
    setFormData({
      companyId: companyId || '',
      thirdPartyName: '',
      description: '',
      allowedApis: [],
      metadata: ''
    });
    setAllowedApisInput('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAllowedApisChange = (e) => {
    const value = e.target.value;
    setAllowedApisInput(value);
    const apis = value.split(',').map(api => api.trim()).filter(api => api.length > 0);
    setFormData(prev => ({
      ...prev,
      allowedApis: apis
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.companyId) {
      toast.warning(t("ThirdPartyClientModal.validation.companyId.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!formData.thirdPartyName || !formData.thirdPartyName.trim()) {
      toast.warning(t("ThirdPartyClientModal.validation.thirdPartyName.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!formData.allowedApis || formData.allowedApis.length === 0) {
      toast.warning(t("ThirdPartyClientModal.validation.allowedApis.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setLoading(true);
    const payload = {
      companyId: formData.companyId,
      thirdPartyName: formData.thirdPartyName.trim(),
      description: formData.description.trim() || null,
      allowedApis: formData.allowedApis,
      metadata: formData.metadata.trim() || null
    };

    const apiCall = clientId
      ? ThirdPartyClientService.updateClient(clientId, payload)
      : ThirdPartyClientService.createClient(payload);

    apiCall
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success(t(clientId ? "ThirdPartyClientModal.updateSuccess.label" : "ThirdPartyClientModal.createSuccess.label"), {
            position: toast.POSITION.TOP_RIGHT,
          });
          const credentials = !clientId && response.data ? {
            clientId: response.data.clientId,
            clientSecret: response.data.clientSecret
          } : null;
          onSuccess(credentials);
          onHide();
        }
      })
      .catch((error) => {
        toast.error(t(clientId ? "ThirdPartyClientModal.updateError.label" : "ThirdPartyClientModal.createError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" data-testid="third-party-client-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t(clientId ? "ThirdPartyClientModal.editTitle.label" : "ThirdPartyClientModal.createTitle.label")}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t("company.label")} <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={displayCompanyName || ''}
              disabled
              data-testid="company-name-input"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("ThirdPartyClientModal.thirdPartyName.label")} <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="thirdPartyName"
              value={formData.thirdPartyName}
              onChange={handleInputChange}
              required
              data-testid="third-party-name-input"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("Description.label")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              data-testid="description-input"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("ThirdPartyClientModal.allowedApis.label")} <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={allowedApisInput}
              onChange={handleAllowedApisChange}
              placeholder={t("ThirdPartyClientModal.allowedApis.placeholder.label")}
              required
              data-testid="allowed-apis-input"
            />
            <Form.Text className="text-muted">
              {t("ThirdPartyClientModal.allowedApis.help.label")}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("ThirdPartyClientModal.metadata.label")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="metadata"
              value={formData.metadata}
              onChange={handleInputChange}
              data-testid="metadata-input"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading} data-testid="cancel-button">
            {t("Cancel.label")}
          </Button>
          <Button variant="primary" type="submit" disabled={loading} data-testid="submit-button">
            {loading ? t("ThirdPartyClientModal.saving.label") : (clientId ? t("Update.label") : t("Create.label"))}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default withTranslation()(ThirdPartyClientModal);
